import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { dataApi } from '@/shared/api/dataApi';
import { dialogs } from '@/shared/ui/dialogStore';
import { toast } from '@/shared/ui/toastStore';
import { eventBus } from '@/shared/realtime/eventBus';
import { useRealtimeStore } from '@/shared/realtime/systemHub';
import { useAuthStore } from '@/entities/session/model/authStore';
import {
  beginEdit,
  endEdit,
  joinScreen,
  leaveScreen,
  othersOf,
  rowEditors,
  usePresenceStore,
  type PresenceUser,
} from './presenceStore';

const EMPTY: PresenceUser[] = [];

/** Ekrandaki diğer kişiler (kişi başına bir kayıt; kendi sekmelerim hariç). */
export interface PresencePerson {
  userId: string;
  userCode: string;
  userName: string;
  /** Düzenlediği satır(lar) varsa ilk düzenleme başlangıcı */
  editingSince: string | null;
  editing: boolean;
}

export function groupPeople(users: PresenceUser[], myUserId: string | null): PresencePerson[] {
  const map = new Map<string, PresencePerson>();
  for (const u of users) {
    if (myUserId && u.userId.toLowerCase() === myUserId.toLowerCase()) continue;
    const key = u.userId.toLowerCase();
    const p = map.get(key) ?? {
      userId: u.userId,
      userCode: u.userCode,
      userName: u.userName || u.userCode,
      editingSince: null,
      editing: false,
    };
    if (u.editingKey) {
      p.editing = true;
      if (!p.editingSince || (u.editingSince && u.editingSince < p.editingSince))
        p.editingSince = u.editingSince;
    }
    map.set(key, p);
  }
  return [...map.values()];
}

/** Ekrana katılır (bileşen ömrü boyunca) ve diğer kişileri döndürür. */
export function useScreenPresence(objectId: string | null | undefined, enabled = true): PresencePerson[] {
  const id = enabled && objectId ? objectId.toLowerCase() : null;
  useEffect(() => {
    if (!id) return;
    joinScreen(id);
    return () => leaveScreen(id);
  }, [id]);
  // Bağlantı kimliği değişince "ben" yeniden hesaplanmalı.
  const hubState = useRealtimeStore((s) => s.system);
  const users = usePresenceStore((s) => (id ? s.byObject[id] : undefined) ?? EMPTY);
  const myUserId = useAuthStore((s) => s.user?.userId ?? null);
  // hubState: yeniden bağlanınca bağlantı kimliği (othersOf'taki "ben") değişir.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => groupPeople(othersOf(users), myUserId), [users, myUserId, hubState]);
}

/** Satır anahtarı → o satırı düzenleyen diğer bağlantılar (satır rozeti için). */
export function useRowEditors(objectId: string | null | undefined): Map<string, PresenceUser[]> {
  const id = objectId?.toLowerCase() ?? '';
  const users = usePresenceStore((s) => s.byObject[id] ?? EMPTY);
  const hubState = useRealtimeStore((s) => s.system);
  return useMemo(() => {
    const map = new Map<string, PresenceUser[]>();
    for (const u of othersOf(users)) {
      if (!u.editingKey) continue;
      map.set(u.editingKey, [...(map.get(u.editingKey) ?? []), u]);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, hubState]);
}

function minutesSince(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : Math.max(0, Math.round((Date.now() - t) / 60_000));
}

export function editorLabel(u: PresenceUser, t: TFunction): string {
  const me = useAuthStore.getState().user?.userId?.toLowerCase();
  const name = u.userName || u.userCode;
  return me && u.userId.toLowerCase() === me ? t('presence.youOtherTab') : name;
}

function editorsText(list: PresenceUser[], t: TFunction): string {
  return list
    .map((u) => {
      const min = minutesSince(u.editingSince);
      return min > 0 ? t('presence.editorSince', { name: editorLabel(u, t), count: min }) : editorLabel(u, t);
    })
    .join(', ');
}

/**
 * Düzenlemeye girmeden önce: satırı başkası düzenliyorsa uyarı + onay.
 * true → devam; false → vazgeç.
 */
export async function confirmEditConflict(
  objectId: string | null | undefined,
  rowKey: string | null | undefined,
  t: TFunction,
): Promise<boolean> {
  if (!objectId || !rowKey) return true;
  const list = rowEditors(objectId, rowKey);
  if (!list.length) return true;
  return dialogs.confirm({
    title: t('presence.conflictTitle'),
    message: t('presence.conflictMessage', { editors: editorsText(list, t) }),
    okText: t('presence.editAnyway'),
  });
}

/**
 * Düzenleme yüzeyi açıkken (form, satır içi düzenleme) satırı "düzenleniyor" olarak bildirir.
 * Yerel liste henüz güncellenmediği için onay penceresi atlanmışsa sunucunun döndürdüğü
 * listeyle bildirim verilir.
 */
export function useEditPresence(objectId: string | null | undefined, rowKey: string | null | undefined) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const id = objectId?.toLowerCase() ?? '';
  const key = rowKey ?? '';
  useEffect(() => {
    if (!id || !key) return;
    const warned = rowEditors(id, key).length > 0;
    let alive = true;
    void beginEdit(id, key).then((others) => {
      if (alive && !warned && others.length)
        toast.warning(
          tRef.current('presence.conflictTitle'),
          tRef.current('presence.alsoEditing', { editors: editorsText(others, tRef.current) }),
        );
    });
    return () => {
      alive = false;
      endEdit(id, key);
    };
  }, [id, key]);
}

/** Uygulama genelinde: başkası aynı satırı düzenlemeye başladı / kaydetti bildirimleri. */
export function usePresenceNotifications() {
  const { t } = useTranslation();
  useEffect(() => {
    const onConflict = ({ user }: { user: PresenceUser }) =>
      toast.warning(
        t('presence.conflictTitle'),
        t('presence.startedEditing', { name: editorLabel(user, t) }),
      );
    const onSaved = ({ user }: { user: PresenceUser }) =>
      toast.warning(t('presence.savedTitle'), t('presence.savedByOther', { name: editorLabel(user, t) }));
    eventBus.on('EditConflict', onConflict);
    eventBus.on('RowSaved', onSaved);
    return () => {
      eventBus.off('EditConflict', onConflict);
      eventBus.off('RowSaved', onSaved);
    };
  }, [t]);
}

/** Profil fotoğrafı (avatar yığını için) — kişi başına bir kez çekilir. */
export function useUserPhoto(userId: string) {
  return useQuery({
    queryKey: ['presence', 'photo', userId.toLowerCase()],
    queryFn: () => dataApi.getUserProfileImage(userId),
    enabled: !!userId,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: false,
  }).data;
}

export function minutesAgo(iso: string | null) {
  return minutesSince(iso);
}
